import React, { useMemo } from "react";
import { Field } from "react-final-form";

import { Select as SelectBase } from "../Inputs/Select/Select";
import { Checkbox as CheckboxBase } from "../Inputs/Checkbox/Checkbox";
import { SwitchBox as SwitchBoxBase } from "../Inputs/SwitchBox/SwitchBox";
import { TextInput } from "../Inputs/TextInput/TextInput";
import { TextArea } from "../Inputs/TextArea/TextArea";
import TextEditor from "../Inputs/TextEditor";

import { composeValidators, createRequiredValidator, validateEmail, validatePassword } from "./validator";

const parse = value => (isNaN(parseFloat(value)) ? value : parseFloat(value));

export const TextField = ({
    label,
    isRequired,
    validator,
    type,
    errorMessage,
    ...restFieldProps
}) => {
    const validatorField = useMemo(() => {
        let validatorsRoot = [];

        if(isRequired){
            validatorsRoot.push(createRequiredValidator(errorMessage || label));
        }

        if(validator){
            validatorsRoot.push(validator);
        }

        return composeValidators(...validatorsRoot);
    }, [ isRequired, validator, errorMessage, label ]);

    return (
        <Field
            label={label}
            validate={validatorField}
            parse={type === "number" ? parse : undefined}
            {...restFieldProps}
        >
            { ({ input, meta, ...restProps }) => {
                // meta.dirty was removed cuz of showing an error after adding new input without changing it
                // const errorMessage = ((meta.dirty || meta.touched || meta.submitFailed) && meta.error) ? meta.error : "";
                const errorMessage = ((meta.touched || meta.submitFailed) && meta.error) ? meta.error : "";

                return (
                    <TextInput
                        {...input}
                        {...restProps}
                        type={type}
                        error={errorMessage}
                    />
                );
            } }
        </Field>
    );
};

export const TextAreaField = ({ label, isRequired, rows, validator, errorMessage, autosize = false, maxRows= 10, ...restFieldProps }) => {
    const validatorField = useMemo(() => {
        let validatorsRoot = [];

        if(isRequired){
            validatorsRoot.push(createRequiredValidator(errorMessage || label));
        }

        if(validator){
            validatorsRoot.push(validator);
        }

        return composeValidators(...validatorsRoot);
    }, [ isRequired, validator, errorMessage, label ]);

    return (
        <Field
            label={label}
            validate={validatorField}
            {...restFieldProps}
        >
            { ({ input, meta, ...restProps }) => {
                const errorMessage = ((meta.dirty || meta.touched || meta.submitFailed) && meta.error) ? meta.error : "";

                return (
                    <TextArea
                        {...input}
                        {...restProps}
                        autosize
                        error={errorMessage}
                    />
                );
            } }
        </Field>
    );
};

export const PasswordField = ({
    label = "Password",
    name = "password",
    placeholder = "Password",
    ...restProps
}) => {
    const validator = useMemo(() => (
        composeValidators(createRequiredValidator(label), validatePassword)
    ), [ label ]);

    return (
        <TextField
            name={name}
            type="password"
            label={label}
            placeholder={placeholder}
            validate={validator}
            {...restProps}
        />
    );
};

export const EmailField = ({ label = "Email", placeholder = "Email", ...restProps }) => {
    const validator = useMemo(() => (
        composeValidators(createRequiredValidator(label), validateEmail)
    ), [ label ]);

    return (
        <TextField
            name="email"
            label={label}
            placeholder={placeholder}
            validate={validator}
            {...restProps}
        />
    );
};

export const SelectField = ({ label, isRequired, ...restProps }) => (
    <Field
        {...restProps}
        label={label}
        validate={isRequired && createRequiredValidator(label)}
        component={Select}
    />
);

export const Select = ({
    input = {},
    meta,
    options,
    ...restProps
}) => {
    const errorMessage = meta.touched && (meta.dirty || meta.submitFailed) ? meta.error : "";

    return (
        <SelectBase
            {...input}
            {...restProps}
            options={options}
            error={errorMessage}
        />
    );
};

export const CheckboxField = ({ label, isRequired, ...restFieldProps }) => {
    const validator = useMemo(() => (
        isRequired ? createRequiredValidator(label) : () => undefined
    ), [ label, isRequired ]);

    return (
        <Field
            label={label}
            validate={validator}
            component={Checkbox}
            {...restFieldProps}
        />
    );
};

export const Checkbox = ({
    input,
    meta,
    ...rest
}) => {
    const errorMessage = meta.touched && (meta.dirty || meta.submitFailed) ? meta.error : "";

    return (
        <>
            <CheckboxBase
                {...input}
                {...rest}
                value={!!input.value}
            />
            <div className="checkbox__helper-message">{ errorMessage }</div>
        </>
    );
};

export const SwitchBoxField = ({ label, isRequired, ...restFieldProps }) => {
    const validator = useMemo(() => (
        isRequired ? createRequiredValidator(label) : () => undefined
    ), [ label, isRequired ]);

    return (
        <Field
            label={label}
            validate={validator}
            component={SwitchBox}
            {...restFieldProps}
        />
    );
};

export const SwitchBox = ({
    input,
    ...rest
}) => (
    <SwitchBoxBase
        {...input}
        {...rest}
        checked={!!input.value}
    />
);

export const TextEditorField = ({ label, isRequired, ...restFieldProps }) => {
    const validator = useMemo(() => (
        isRequired ? createRequiredValidator(label) : () => undefined
    ), [ label, isRequired ]);

    return (
        <Field
            label={label}
            validate={validator}
            {...restFieldProps}
        >
            { ({ input, meta, ...restProps }) => {
                const errorMessage = meta.touched && (meta.dirty || meta.submitFailed) ? meta.error : "";

                return (
                    <TextEditor
                        {...input}
                        {...restFieldProps}
                        setContents={input?.value}
                        onChange={input?.onChange}
                        error={errorMessage}
                    />
                );
            } }
        </Field>
    );
};
